import { ReactNode, useState } from "react";
import styles from "./OrderInfoBanner.module.scss";
import { FormattedMessage } from "react-intl";
import MiscellaneousWarning from "common/icons/MiscellaneousWarning.tsx";
import MiscellaneousPin from "common/icons/MiscellaneousPin.tsx";
import MiscellaneousCheckmarkCircleFill from "common/icons/MiscellaneousCheckmarkCircleFill.tsx";
import MiscellaneousExclamationCircle from "common/icons/MiscellaneousExclamationCircle.tsx";
import { CourseMaterial } from "../coursesLoader.ts";
import BannerContainer from "common/core-ui/BannerContainer.tsx";
import useOrderInfo from "../useOrderInfo.tsx";
import clsx from "clsx";
import BaseLinkOrButton from "../../../../../common/core-ui/BaseLinkOrButton.tsx";
import { ShippingAddress } from "common/api/decimal/methods/course.ts";
import ShippingAddressDialog from "../ShippingAddress/ShippingAddressDialog.tsx";
import { useBrandLoaderData } from "routes/root/rootLoader.ts";

export default function OrderInfoBanner({
  courseMaterial,
  preferredAddress,
}: {
  courseMaterial: CourseMaterial;
  preferredAddress?: ShippingAddress;
}) {
  const {
    orderStatus,
    studentEmail,
    phone,
    address,
    isCampusAddress,
    mapDirectionsUrl,
    deliveryStatusDate,
  } = useOrderInfo(courseMaterial);
  const [showingAddressDialog, setShowingAddressDialog] = useState(false);
  const brand = useBrandLoaderData();

  const getFormattedMessageValues = ({
    address,
    anchorUrl,
    studentEmail,
    phone,
    date,
  }: {
    address?: ShippingAddress;
    anchorUrl?: string;
    studentEmail?: string;
    phone?: string;
    date?: string;
  }) => ({
    strong: (chunks: ReactNode) => <strong>{chunks}</strong>,
    p: (chunks: ReactNode) => <p>{chunks}</p>,
    address: `${address?.addressLine1} ${address?.addressLine2}`,
    cityStateZip: `${address?.city}, ${address?.state} ${address?.zip}`,
    anchor: (chunks: ReactNode) => (
      <BaseLinkOrButton
        href={anchorUrl}
        className={clsx("vst-link", "vst-link-size-inherit", styles.anchor)}
      >
        {chunks}
      </BaseLinkOrButton>
    ),
    updateAddress: (chunks: ReactNode) => (
      <BaseLinkOrButton
        className={clsx("vst-link", "vst-link-size-inherit", styles.anchor)}
        onClick={() => setShowingAddressDialog(true)}
      >
        {chunks}
      </BaseLinkOrButton>
    ),
    studentEmail,
    phoneTag: () => (phone ? <p>{phone}</p> : null),
    date: !!date && new Date(date),
  });

  if (orderStatus === "Needs to be Returned") {
    return (
      <BannerContainer
        icon={<MiscellaneousExclamationCircle />}
        className={clsx(styles.container, styles.red)}
        size="small"
      >
        {isCampusAddress ? (
          <FormattedMessage
            defaultMessage={
              "<strong>Return Item</strong><p>This item needs to be returned. An email was sent to {studentEmail} with the return location details. Please note, the return location may be different from the pickup location.</p>"
            }
            values={getFormattedMessageValues({ studentEmail })}
          />
        ) : (
          <FormattedMessage
            defaultMessage="<strong>Return Item</strong><p>This item needs to be returned. An email was sent to {studentEmail} with your shipping label for return.</p>"
            values={getFormattedMessageValues({ studentEmail })}
          />
        )}
      </BannerContainer>
    );
  }

  if (orderStatus === "Ready for Pickup") {
    return (
      <BannerContainer
        icon={<MiscellaneousPin />}
        className={clsx(styles.container, styles.green)}
        size="small"
      >
        <FormattedMessage
          defaultMessage={
            "<strong>Pickup Info</strong><p>Please pick up your items at your campus location:</p><p>{address}</p><p>{cityStateZip}</p><phoneTag></phoneTag><anchor>View Map & Directions</anchor>"
          }
          values={getFormattedMessageValues({
            address,
            anchorUrl: mapDirectionsUrl,
            phone,
          })}
        />
      </BannerContainer>
    );
  }

  if (
    orderStatus === "Awaiting Order" &&
    brand.VirtualStore.EA.enableShippingAddressChanges
  ) {
    return (
      <>
        <BannerContainer
          icon={
            preferredAddress ? (
              <MiscellaneousWarning />
            ) : (
              <MiscellaneousExclamationCircle />
            )
          }
          className={clsx(
            styles.container,
            preferredAddress ? styles.yellow : styles.red
          )}
          size="small"
        >
          {preferredAddress ? (
            <FormattedMessage
              defaultMessage={
                "<strong>Confirm Your Shipping Address</strong><p>Physical items are being shipped to:</p><p>{address}</p><p>{cityStateZip}</p><updateAddress>Update</updateAddress>"
              }
              values={getFormattedMessageValues({
                address: preferredAddress,
              })}
            />
          ) : (
            <FormattedMessage
              defaultMessage={
                "<strong>Add Shipping Address</strong><p>Add a shipping address to receive your materials on time and at the right place.</p><updateAddress>Add Address</updateAddress>"
              }
              values={getFormattedMessageValues({})}
            />
          )}
        </BannerContainer>
        {showingAddressDialog && (
          <ShippingAddressDialog
            onRequestClose={() => setShowingAddressDialog(false)}
          />
        )}
      </>
    );
  }

  if (orderStatus === "Preparing to Ship" || orderStatus === "Shipped") {
    return (
      <BannerContainer
        icon={<MiscellaneousPin />}
        className={clsx(styles.container, styles.green)}
        size="small"
      >
        {isCampusAddress ? (
          <FormattedMessage
            defaultMessage={
              "<strong>Shipping</strong><p>Physical items are being shipped to:</p><p>{address}</p><p>{cityStateZip}</p><phoneTag></phoneTag><anchor>View Map & Directions</anchor>"
            }
            values={getFormattedMessageValues({
              address,
              anchorUrl: mapDirectionsUrl,
              phone,
            })}
          />
        ) : (
          <FormattedMessage
            defaultMessage={
              "<strong>Shipping</strong><p>Physical items are being shipped to:</p><p>{address}</p><p>{cityStateZip}</p>"
            }
            values={getFormattedMessageValues({
              address,
            })}
          />
        )}
      </BannerContainer>
    );
  }

  if (orderStatus === "Picked Up") {
    return (
      <BannerContainer
        icon={<MiscellaneousCheckmarkCircleFill />}
        className={clsx(styles.container, styles.green)}
        size="small"
      >
        <FormattedMessage
          defaultMessage={
            "<strong>Picked Up</strong><p>Your item was picked up on {date, date, long}</p>"
          }
          values={getFormattedMessageValues({
            date: deliveryStatusDate,
          })}
        />
      </BannerContainer>
    );
  }

  return null;
}
